import React, { useMemo, useState, useRef } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, parseISO, addHours } from "date-fns";
import { enUS } from "date-fns/locale";
import type { ScheduleItem, Note } from "types";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales: { "en-US": enUS },
});

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: ScheduleItem;
  isPickup: boolean;
  courierColor: string | undefined;
  courierName: string | undefined;
  notes: Note[];
}

interface Props {
  items:          ScheduleItem[];
  weekStart:      Date;
  partnerNames:   Record<number, string>;
  partnerTypes:   Record<number, string>;
  courierInfo:    { names: Record<number, string>; colors: Record<number, string> };
  notesByPartner: Record<number, Note[]>;
  onSelectEvent:  (itemId: number) => void;
}

const UNASSIGNED_ID = null;
const UNASSIGNED_COLOR = "#9ca3af";

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// Arrow-up from a box — "collecting" from a donor
function PickupIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <polyline points="4,7 8,2 12,7" />
      <line x1="8" y1="2" x2="8" y2="12" />
      <rect x="2" y="12" width="12" height="3" rx="1" />
    </svg>
  );
}

// Arrow-down to a box — "delivering" to a recipient
function DropoffIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <polyline points="4,5 8,10 12,5" />
      <line x1="8" y1="2" x2="8" y2="10" />
      <rect x="2" y="12" width="12" height="3" rx="1" />
    </svg>
  );
}

function CalendarEventTile({ event }: { event: CalendarEvent }) {
  const label = event.isPickup ? "Pickup" : "Drop off";
  const tileRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  const handleMouseEnter = () => {
    if (!tileRef.current) return;
    const rect = tileRef.current.getBoundingClientRect();
    // Position tooltip to the right of the event; flip left if near right edge
    const spaceRight = window.innerWidth - rect.right;
    const left = spaceRight > 220 ? rect.right + 6 : rect.left - 214;
    setHovered(true);
    setTooltipPos({ top: rect.top, left });
  };

  const handleMouseLeave = () => {
    setHovered(false);
    setTooltipPos(null);
  };

  return (
    <>
      <div
        ref={tileRef}
        className="h-full flex flex-col gap-0.5 overflow-hidden leading-tight rounded transition-all duration-100"
        style={{
          outlineOffset: "-1px",
          filter: hovered ? "brightness(0.93)" : "none",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className={`flex items-center gap-1 text-xs font-semibold truncate ${event.isPickup ? "text-green-700" : "text-indigo-700"}`}>
          {event.isPickup ? <PickupIcon /> : <DropoffIcon />}
          <span className="truncate">{event.title}</span>
        </span>
      </div>

      {tooltipPos && typeof document !== "undefined" && (
        <div
          className="fixed z-50 w-52 bg-white rounded-lg shadow-lg border border-gray-200 p-3 pointer-events-none"
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          <div className={`flex items-center gap-1.5 mb-2 text-xs font-semibold ${event.isPickup ? "text-green-700" : "text-indigo-700"}`}>
            {event.isPickup ? <PickupIcon /> : <DropoffIcon />}
            <span>{label}</span>
          </div>
          <p className="text-sm font-medium text-gray-900 mb-1">{event.title}</p>
          <p className="text-xs text-gray-500 mb-2">
            {formatTime(event.start)} – {formatTime(event.end)}
          </p>
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: event.courierColor ?? UNASSIGNED_COLOR }}
            />
            <span className="text-xs font-medium" style={{ color: event.courierColor ?? UNASSIGNED_COLOR }}>
              {event.courierName ?? "Unassigned"}
            </span>
          </div>
          {event.notes.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
              {event.notes.map((n) => (
                <p key={n.id} className="text-xs text-gray-500 leading-snug">{n.note}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function CalendarView({ items, weekStart, partnerNames, partnerTypes, courierInfo, notesByPartner, onSelectEvent }: Props) {
  const [hiddenCouriers, setHiddenCouriers] = useState<Set<number | null>>(new Set());

  const events = useMemo<CalendarEvent[]>(() => {
    return items.map((item) => {
      const partnerName = partnerNames[item.partner_entity_id] ?? `Partner #${item.partner_entity_id}`;
      const isPickup = partnerTypes[item.partner_entity_id] !== "distributor";
      const start = parseISO(item.scheduled_at);
      const end = addHours(start, 1);
      const courierColor = item.contact_user_id ? courierInfo.colors[item.contact_user_id] : undefined;
      const courierName = item.contact_user_id ? courierInfo.names[item.contact_user_id] : undefined;
      const allNotes = notesByPartner[item.partner_entity_id] ?? [];
      const notes = allNotes.filter((n) => !n.schedule_item_id || n.schedule_item_id === item.id);

      return {
        id: item.id,
        title: partnerName,
        start,
        end,
        resource: item,
        isPickup,
        courierColor,
        courierName,
        notes,
      } as CalendarEvent;
    });
  }, [items, partnerNames, partnerTypes, courierInfo, notesByPartner]);

  // Legend: one entry per unique courier present in events, plus "Unassigned" if any
  const legendItems = useMemo(() => {
    const seen = new Map<number | null, { name: string; color: string }>();
    events.forEach((event) => {
      const id = event.resource.contact_user_id ?? UNASSIGNED_ID;
      if (!seen.has(id)) {
        seen.set(id, {
          name: id !== null ? (courierInfo.names[id] ?? `Courier #${id}`) : "Unassigned",
          color: id !== null ? (courierInfo.colors[id] ?? UNASSIGNED_COLOR) : UNASSIGNED_COLOR,
        });
      }
    });
    // Sort: Unassigned last
    return Array.from(seen.entries())
      .sort(([a], [b]) => {
        if (a === null) return 1;
        if (b === null) return -1;
        return 0;
      })
      .map(([id, info]) => ({ id, ...info }));
  }, [events, courierInfo]);

  const visibleEvents = useMemo(() => {
    if (hiddenCouriers.size === 0) return events;
    return events.filter((event) => {
      const courierId = event.resource.contact_user_id ?? UNASSIGNED_ID;
      return !hiddenCouriers.has(courierId);
    });
  }, [events, hiddenCouriers]);

  const toggleCourier = (courierId: number | null) => {
    setHiddenCouriers((prev) => {
      const next = new Set(prev);
      if (next.has(courierId)) next.delete(courierId);
      else next.add(courierId);
      return next;
    });
  };

  const eventPropGetter = (event: CalendarEvent) => ({
    style: {
      backgroundColor: event.courierColor ? (event.isPickup ? "#f0fdf4" : "#eef2ff") : "#ebebeb",
      borderLeft: `10px solid ${event.courierColor ?? '#8c8c8c'}`,
      borderTop: "none",
      borderRight: "none",
      borderBottom: "none",
      borderRadius: "1px",
      color: "#1f2937",
      padding: "2px 4px",
      cursor: "pointer",
    },
  });

  const components = useMemo(() => ({
    toolbar: () => null,
    event: CalendarEventTile,
  }), []);

  return (
    <div className="mt-6">
      {/* Courier legend */}
      {legendItems.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {legendItems.map(({ id, name, color }) => {
            const hidden = hiddenCouriers.has(id);
            return (
              <button
                key={id ?? "unassigned"}
                onClick={() => toggleCourier(id)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                  hidden
                    ? "border-gray-200 bg-white text-gray-400 opacity-50"
                    : "border-gray-200 bg-white text-gray-700"
                }`}
                title={hidden ? `Show ${name}` : `Hide ${name}`}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: hidden ? "#d1d5db" : color }}
                />
                {name}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ height: "calc(100vh - 260px)", minHeight: 500 }}>
        <Calendar<CalendarEvent>
          localizer={localizer}
          events={visibleEvents}
          view={Views.WEEK}
          date={weekStart}
          onNavigate={() => {}}
          onView={() => {}}
          startAccessor="start"
          endAccessor="end"
          titleAccessor="title"
          components={components}
          eventPropGetter={eventPropGetter}
          onSelectEvent={(event) => onSelectEvent(event.id)}
          step={30}
          timeslots={2}
        />
      </div>
    </div>
  );
}
